import React, { useState, useEffect } from 'react';
import { BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import Api from '../../../api/global.service';
import BlockUi from 'react-block-ui';
import formatMoneyNumber, { formatNumber } from '../../../utils/formatNumber.js';

import { useCoopConfig } from '../../content/coop-config';
import convertableUnitsObject from '../../../utils/convertableUnitsObject';
import EmptyStateChart from '../empty-state-chart';

const ParcelasResumenCosechaTotal = ({ id_ciclo }) => {
  const [dataGenero, setDataGenero] = useState(null);
  const [blocking, setBlocking] = useState(true);
  const [weightUnit, setWeightUnit] = useState('kg');
  const coopConfig = useCoopConfig();

  useEffect(() => {
    if (id_ciclo > 0) {
      getData();
    }
  }, [id_ciclo]);

  useEffect(() => {
    if (coopConfig.acopioUnits()) {
      setWeightUnit(coopConfig.hasAcopioUnitsExtra() ? coopConfig.acopioUnitsExtra()?.abreviacion : coopConfig.acopioUnits()?.abreviacion);
    }
  }, [coopConfig.config]);

  const buildGeneroData = d => {
    const converted = convertableUnitsObject(d, coopConfig, ['estimacion_mujer', 'estimacion_hombre', 'estimacion_sin_asignar']);
    const mujeres = parseFloat(converted.estimacion_mujer) || 0;
    const hombres = parseFloat(converted.estimacion_hombre) || 0;
    const sinAsignar = parseFloat(converted.estimacion_sin_asignar) || 0;
    return {
      name: 'Estimación de cosecha',
      mujeres,
      hombres,
      sinAsignar,
      total: mujeres + hombres + sinAsignar,
    };
  };

  const getData = async () => {
    setBlocking(true);
    let d = null;
    try {
      const response = await Api.search('dashboards/productores/estimacion', {
        filter: [{ column: 'id_ciclo', operator: '=', value: id_ciclo }],
      });
      d = response.data[0];
    } catch (e) {
      d = null;
    }

    const tieneDatos =
      d && (parseFloat(d.estimacion_mujer) > 0 || parseFloat(d.estimacion_hombre) > 0 || parseFloat(d.estimacion_sin_asignar) > 0);
    setDataGenero(tieneDatos ? buildGeneroData(d) : null);
    setBlocking(false);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: 'white',
            borderColor: '#cacaca',
            borderWidth: 1,
            borderStyle: 'solid',
            padding: 5,
            minWidth: 200,
          }}>
          <p className="mb-1">
            Mujeres:{' '}
            <b>
              {formatMoneyNumber(data.mujeres)} {weightUnit}
            </b>
          </p>
          <p className="mb-1">
            Hombres:{' '}
            <b>
              {formatMoneyNumber(data.hombres)} {weightUnit}
            </b>
          </p>
          <p className="mb-1">
            Sin asignar:{' '}
            <b>
              {formatMoneyNumber(data.sinAsignar)} {weightUnit}
            </b>
          </p>
          <p className="mb-0">
            Total:{' '}
            <b>
              {formatMoneyNumber(data.total)} {weightUnit}
            </b>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <BlockUi tag="div" message="" blocking={blocking} renderChildren={true}>
      <div className="row card-table">
        <h3 className="dashboard-titulo-grafica mb-3">Estimación de cosecha</h3>
        <div className="col-md-12">
          {!blocking && !dataGenero && <EmptyStateChart />}
          {dataGenero && (
            <div className="row mt-2 mb-3">
              <div className="col-12">
                <div className="font-lato-normal-400" style={{ fontSize: 14 }}>
                  Estimación total de cosecha
                </div>
                <div className="font-lato-normal-600" style={{ fontSize: 32, fontVariantNumeric: 'tabular-nums' }}>
                  {formatNumber(dataGenero.total)} {weightUnit}
                </div>
              </div>
            </div>
          )}
          {dataGenero && (
            <div className="row mt-2">
              <div className="col-md-12">
                <ResponsiveContainer width="100%" height={90}>
                  <BarChart layout="vertical" data={[dataGenero]} margin={{ left: -60 }} stackOffset="expand">
                    <CartesianGrid horizontal={false} vertical={false} strokeDasharray="3 3" />
                    <XAxis hide type="number" />
                    <YAxis type="category" dataKey="name" fontSize="14" stroke="#fff" />
                    <Tooltip shared={true} content={<CustomTooltip />} />
                    <Bar dataKey="mujeres" stackId="a" fill="#B6D7A8" name="Mujeres" radius={[10, 0, 0, 10]} isAnimationActive={false} />
                    <Bar dataKey="hombres" stackId="a" fill="#6AA84F" name="Hombres" isAnimationActive={false} />
                    <Bar
                      dataKey="sinAsignar"
                      stackId="a"
                      fill="#38761D"
                      name="Sin asignar"
                      radius={[0, 10, 10, 0]}
                      isAnimationActive={false}
                    />
                  </BarChart>
                </ResponsiveContainer>
                <div className="row">
                  <div className="col-md-4">
                    <i className="dot-chart" style={{ backgroundColor: '#B6D7A8' }}></i>
                    <div className="ml-4 text-left">
                      <h5 className="font-lato-normal-400 mb-0" style={{ fontSize: '1.5rem' }}>
                        {formatNumber(dataGenero.mujeres)} {weightUnit}
                      </h5>
                      <h6 className="font-lato-normal-400 pt-0">Mujeres</h6>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <i className="dot-chart" style={{ backgroundColor: '#6AA84F' }}></i>
                    <div className="ml-4 text-left">
                      <h5 className="font-lato-normal-400 mb-0" style={{ fontSize: '1.5rem' }}>
                        {formatNumber(dataGenero.hombres)} {weightUnit}
                      </h5>
                      <h6 className="font-lato-normal-400 pt-0">Hombres</h6>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <i className="dot-chart" style={{ backgroundColor: '#38761D' }}></i>
                    <div className="ml-4 text-left">
                      <h5 className="font-lato-normal-400 mb-0" style={{ fontSize: '1.5rem' }}>
                        {formatNumber(dataGenero.sinAsignar)} {weightUnit}
                      </h5>
                      <h6 className="font-lato-normal-400 pt-0">Sin asignar</h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </BlockUi>
  );
};

export default ParcelasResumenCosechaTotal;
